import React, { useState, useEffect, Suspense, startTransition } from "react";
import styles from "./reconciliationTagTile.module.scss";
import {
  Grid,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Button,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import Icon from "@mdi/react";
import { mdiAlertCircleOutline, mdiCalculator, mdiChevronDown } from "@mdi/js";
import { AddCountDialog, Calculator, CountEntryTable } from "../..";
import { ConfirmationDialog, CustomTooltip, Loader } from "../../../components";
import { IReconciliationTagTileResponse } from "../../../interfaces/IReconciliationTagTileResponse";
import { useLocation } from "react-router-dom";
import { IAdminException, IAdminExceptionProps, IContainerizedDetail, ICountEntry, IUpdateReconcileDetailsRequest } from "../../../interfaces";
import { AdminExceptionHelper, useException } from "../../../utils";
import { useClient } from "../../../utils/ClientContext";
import { containerizedDetailService ,tagService} from "../../../api/services";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

enum ReconciliationTypes {
  Counter,
  Auditer,
}

const ReconciliationTagTile: React.FC = () => {
  const location = useLocation();
  const id = new URLSearchParams(location.search).get("tagid");
  const isLocked = new URLSearchParams(location.search).get("isLocked");
  const isInventoryLocked = isLocked === "true";
  const [openCalculator, setOpenCalculator] = useState(false);
  const [ConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [countValidation, setCountValidation] = useState(false);
  const { setException, clearException } = useException();

  // Create a state variable for the input value
  const [formValues, setFormValues] = useState({
    count: "",
    uom: "",
  });

  const [errors, setErrors] = useState({
    count: false,
    uom: false,
  });

  const [tagDetails, setTagDetails] =
    useState<IReconciliationTagTileResponse | null>(null);
  const [isContainerized, setIsContainerized] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [tagDetailsError, setTagDetailsError] = useState<string | null>(null);
  const [uoms, setUoms] = useState<{ id: number; name: string }[]>([]);
  const [finalType, setFinalType] = useState<ReconciliationTypes | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [adminExceptionProps, setAdminExceptionProps] = useState<IAdminExceptionProps>();
  const [adminException, setAdminException] = useState<IAdminException>();
  const [containerizedDetails, setContainerizedDetails] = useState<IContainerizedDetail>();
  const [actionEditIndex, setActionEditIndex] = useState<number | null>();
  const { client } = useClient();
  const clientId = client.id;
  const [openAddCount, setOpenAddCount] = useState(false);
  const handleAddCountClose = () => setOpenAddCount(false);
  const [isAllowEdit, setIsAllowEdit] = useState<boolean>(false);
  const [editRestrictionMessage, SeteditRestrictionMessage] = useState("");
   const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  useEffect(() => {
    fetchTagDetails();
  }, []);
  useEffect(() => {
    clearException();
  }, [clientId]);

    const fetchContainerizedDetail = async () => {
      if (id) {
        const payload ={
          tagId :+id,
          isOnlyException : false
        }
        const response = await containerizedDetailService.getTagContainerizedDetailAsync(payload);
        if(response.data?.lstContainerizedDetail){
          setContainerizedDetails({
            entries: response.data.lstContainerizedDetail,
            isSerialized: response.data.isSerialized,
            isLotted: response.data.isLotted,
            isCartoned: response.data.isCartoned,
            isPalletized: response.data.isPalletized,
            itemId: response.data.itemId,
            itemName: response.data.itemName,
            locationId: response.data.locationId,
            locationName: response.data.locationName,
            uomId: response.data.uomId,
            uomName: response.data.uomName,
            tagName: response.data.tagName,
          });
        }
      }
    }
    const fetchTagDetails = async () => {
      if (id) {
        setLoading(true);
        setTagDetailsError(null);
        try {
          const response = await tagService.getReconciliationTagAsync(+id);
          setTagDetails(response.data.reconciliationTag);
          setIsContainerized(response.data?.reconciliationTag?.isContainerized);
          setAdminExceptionProps({
            exceptions: response.data.reconciliationTag.exceptions,
            unacceptedExceptions: response.data.reconciliationTag.unacceptedExceptions
          });
          setEditPermission(response.data.reconciliationTag);
          setTimeout(() => {
            fetchContainerizedDetail();
          }, 100);
          if (response.data.reconciliationTag.isReconciliation) {
            const uomResponse = await tagService.getItemUOMAsync(
              response.data.reconciliationTag.itemId
            );
            
            const fetchedUoms = uomResponse.data.unitOfMeasures.map(
              (uom: any) => ({
                id: uom.id,
                name: uom.name,
              })
            );

            setUoms(fetchedUoms); 
            // Select default UOM if only one is available
            if (fetchedUoms.length === 1 && response.data.reconciliationTag.reconcileUOM === "") {
              setFormValues((prevValues) => ({
                ...prevValues,
                uom: fetchedUoms[0].name,
                secondaryUomId: 0,
              }));
            }

            if (
              response.data.reconciliationTag.reconcileUOM !== "" &&
              response.data.reconciliationTag.reconcileQty !== null
            ) {
              setFormValues((prevValues) => ({
                ...prevValues,
                count: response.data.reconciliationTag.reconcileQty,
                uom: response.data.reconciliationTag.reconcileUOM,
              }));
            }
          }
        } catch (err: any) {
          setTagDetailsError(
            err.response?.data?.errors?.[0] ||
              "Failed to fetch reconciliation tag details."
          );
        } finally {
          setLoading(false);
        }
      }
    };
    const setEditPermission=(data:any) =>{
      setIsAllowEdit(data?.isAllowEdit && !isInventoryLocked);
      if (!data.isAllowEdit || isInventoryLocked) {
        let editMessage = "You cannot edit this record since the Audit has to be finished.";
        if(isInventoryLocked){
          editMessage = "You can't edit this record because the inventory is locked."
        }
        SeteditRestrictionMessage(editMessage);
      }
    }
  useEffect(() => {
    if(!adminExceptionProps || !adminExceptionProps?.exceptions){
      clearException();
      setAdminException({
        isException: false,
        tooltipTitle: "",
        tooltipMessage: "",
        tooltipLockMessage:"",
        bannnerTitle:"",
        bannerMessage:"",
        isSerialNoException:false,
        isLotException: false,
        isCartonException: false,
        isPalletException: false
      });
    }
    else{
      let unAcceptedExc = AdminExceptionHelper.adminException(adminExceptionProps, true);
      const ae = AdminExceptionHelper.adminException(adminExceptionProps);
      setAdminException(ae);
    if(unAcceptedExc && unAcceptedExc.isException){
      setException({
        title: unAcceptedExc.bannnerTitle,
        message: unAcceptedExc.bannerMessage
      });
    }
    else{
      clearException();
    }
    }
   
  }, [adminExceptionProps]);
  const handleSubmitTagWithCount = () => {
     setSnackbarMessage("Updated successfully!");
      setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          fetchTagDetails();
        }, 3000);
  };
  const handleExceptionResolveSubmit = () =>{
          fetchTagDetails();
  }
  const handleEdit = (index?: number) => {
    setActionEditIndex(index);
   setOpenAddCount(true);   
};
const handleDelete = (index: number) => {
  console.log("Delete entry at index:", index);
};
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Create an onChange handler
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCountValidation(false);
    const { name, value } = event.target;
    startTransition(() => {
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: value === "",
      }));
    });
  };

  // Inside your AddTagDialog component
  const handleCalculatorValue = (value: string) => {
    startTransition(() => {
      const regex = /^\d{0,8}(\.\d{0,2})?$/;
      if (
        (regex.test(value) || value === "") &&
        parseFloat(value || "0") <= 99999999.99
      ) {
        setFormValues((prevValues) => ({
          ...prevValues,
          count: value,
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          count: false,
        }));
        setIsFormValid(true);
      } else {
        setFormValues((prevValues) => ({
          ...prevValues,
          count: "",
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          count: true,
        }));
        setCountValidation(true);
        setIsFormValid(false);
      }
      setOpenCalculator(false); // Optionally close the dialog after receiving the value
    });
  };

  const onClickUpdateTagSubmit = () => {
    if (uoms.length > 0) {
      const reconcileCount: number = +formValues.count;
      const uOMId: number = uoms.find((x) => x.name == formValues.uom)?.id ?? 0;
      updateReconcileDetails(reconcileCount, uOMId);
    }
  };

  const updateReconcileDetails = async (
    reconcileCount: number,
    uOMId: number
  ) => {
    if (tagDetails != null) {
      const request: IUpdateReconcileDetailsRequest = {
        tagId: tagDetails.tagId,
        reconcileQty: reconcileCount,
        reconcilerUserId: Number(localStorage.getItem("userId")) || 0,
        reconcileUOMId: uOMId,
        clientId: clientId
      };
      try {
        const response = await tagService.updateReconcileDetailsRequest(
          request
        );
        if (response.data && response.data.isSuccess) {
          setSnackbarMessage("Updated successfully!");
          setSnackbarSeverity("success");
          setTimeout(() => {
            fetchTagDetails();
          }, 2000);
        } else {
          setSnackbarMessage("Failed to update reconciliation tag details.");
          setSnackbarSeverity("error");
        }
      } catch (err: any) {
        setSnackbarMessage("Failed to update reconciliation tag details.");
        setSnackbarSeverity("error");
      } finally {
        setSnackbarOpen(true); // Show the snackbar
      }
    }
  };

  const handleCalculatorOpen = () => {
    startTransition(() => setOpenCalculator(true));
  };

  const handleCalculatorClose = () => {
    startTransition(() => setOpenCalculator(false));
  };

  const handleFinalCount = (value: ReconciliationTypes) => {
    setFinalType(value);
    startTransition(() => setConfirmDialogOpen(true));
  };

  if (loading) return <> <Loader/></>;
  if (tagDetailsError) return <div>Error: {tagDetailsError}</div>;

  if (!tagDetails) {
    return <div>No details found for the specified tag ID.</div>;
  }

  return (
    <div className={`${styles.tag_content_container} content_container`}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Grid container size={{xs:12}} className={styles.tagContainerInner}>
        <Grid container size={{xs:12}} className={styles.tagContainerTop}>
          <Grid container className={styles.tagName} size={{sm:12}}>
            <h3>
              <b>Tag ID</b> | #
              {tagDetails.tagNumber?.toString().padStart(2, "0")}&nbsp;&nbsp;
            </h3>
            {
              adminException && adminException.isException 
              && (<CustomTooltip title={adminException.tooltipTitle} tooltipMessage={adminException.tooltipMessage}>
                <IconButton className={styles.exceptionIcon} sx={{ p: 0 }}>
                  <Icon path={mdiAlertCircleOutline} size={1} color={adminExceptionProps?.unacceptedExceptions?.trim()? "red" : "grey"} />
                </IconButton> 
              </CustomTooltip> 
            )}
            <ul>
              <li>
                <span className={styles.labelName}> Area Name </span>
                {tagDetails.areaName}
              </li>
              <li>
                <span className={styles.labelName}> Location Name </span>
                {tagDetails.locationName}
              </li>
            </ul>
          </Grid>
        </Grid>
        <Grid
          container
          size={{xs:12}}
          className={`${styles.tagContainerBottom} ${styles.listContainer}`}
        >
          <ul>
            <li>
  <span className={styles.labelName}>Item ID</span>

  <Tooltip title={tagDetails.itemName} placement="bottom" arrow>
    <span className={styles.tooltip_div}>
      {tagDetails.itemName.length > 20 
        ? tagDetails.itemName.slice(0, 20) + '...' 
        : tagDetails.itemName}
    </span>
  </Tooltip>
</li>

            <li>
              <span className={styles.labelName}> Description </span>
              <Tooltip title={tagDetails.description} placement="bottom" arrow>
                <span className={styles.tooltip_div}>
                  {tagDetails.description}
                </span>
              </Tooltip>
            </li>
            <li>
              <span className={styles.labelName}> UOMs </span>
              {tagDetails.secondaryUOM && tagDetails.secondaryUOM !== ""
                ? tagDetails.secondaryUOM
                : tagDetails.primaryUOM}
            </li>
            <li>
              <span className={styles.labelName}> Cost </span>
              {tagDetails.cost === null ? 0 : tagDetails.cost}
            </li>
            <li>
              <span className={styles.labelName}> UOM </span>
              {tagDetails.primaryUOM}
            </li>
          </ul>
        </Grid>
      </Grid>

      <Grid container size={{xs:12}} className={styles.tagContainerInner}>
        <Grid size={{xs:12}} className={styles.tagContainer_heading}>
          <h4> Count Details </h4>
        </Grid>
        <Grid size={{xs:12}} className={styles.tagContainer_details}>
          <ul>
            <li>
              Count Made
              <b>
                {tagDetails.countMade === null ? "-" : tagDetails.countMade}
              </b>
            </li>
            <li>
              UOM
             <b>{!tagDetails.countUOM ? "-" : tagDetails.countUOM}</b>
            </li>
            <li>
              Counter
              <b>
                {tagDetails.countedBy === null ? "-" : tagDetails.countedBy}
              </b>
            </li>
          </ul>
          {tagDetails.isReconciliation && !isInventoryLocked && !isContainerized && (
            <Button
              variant="outlined" disabled={isContainerized}
              onClick={() => handleFinalCount(ReconciliationTypes.Counter)}
            >
              Use this as final count
            </Button>
          )}
        </Grid>
      </Grid>

      {tagDetails.showAudit && tagDetails.countMade !== null && (
        <Grid container size={{xs:12}} className={styles.tagContainerInner}>
          <Grid size={{xs:12}} className={styles.tagContainer_heading}>
            <h4> Audit Details </h4>
          </Grid>
          <Grid size={{xs:12}} className={styles.tagContainer_details}>
            <ul>
              <li>
                Audit Made
                <b>
                  {tagDetails.auditMade === null ? "-" : tagDetails.auditMade}
                </b>
              </li>
              <li>
                UOM
                <b>
                  {tagDetails.auditUOM === null || tagDetails.auditUOM === ""
                    ? "-"
                    : tagDetails.auditUOM}
                </b>
              </li>
              <li>
                Auditor
                <b>
                  {tagDetails.auditedBy === null ? "-" : tagDetails.auditedBy}
                </b>
              </li>
            </ul>
            {tagDetails.isReconciliation && !isInventoryLocked && !isContainerized && (
              <Button
                variant="outlined" disabled={isContainerized}
                onClick={() => handleFinalCount(ReconciliationTypes.Auditer)}
              >
                Use this as final count
              </Button>
            )}
          </Grid>
        </Grid>
      )}
      {tagDetails.isReconciliation && (
        <Grid container size={{xs:12}} className={styles.reconciliationTagBox}>
          <Grid
            container
            size={{xs:12}}
            className={styles.reconciliationTagBox_heading}
          >
          <p>
           {isContainerized ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <InfoOutlinedIcon  />
                Since the count doesn't match, please reconcile below quantities.
              </span>
            ) : (
              <>Since the quantities don't match, please input a different quantity.</>
            )}
          </p>
          </Grid>
          {!isContainerized && (
          <Grid
            container
            size={{xs:12}}
            className={styles.reconciliationTagBox_form}
          >
            <Grid
              container
              className={styles.fieldContainer}
              size={{xs:12, sm:8}}
            >
              <Grid size={{xs:5.5, sm:4}}>
                <FormControl className="fieldWrapper" fullWidth>
                  <TextField
                    fullWidth
                    name="count"
                    id="count"
                    required
                    value={formValues.count}
                    label="Enter the new quantity"
                    placeholder="Add quantity"
                    className={styles.whiteTextBox}
                    inputProps={{
                      inputMode: "decimal",
                      pattern: "[0-9]*",
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = /^\d{0,8}(\.\d{0,2})?$/;
                      if (
                        (regex.test(value) || value === "") &&
                        parseFloat(value || "0") <= 99999999.99
                      ) {
                        if (value !== "" && formValues.uom !== "") {
                          setIsFormValid(true);
                        } else {
                          setIsFormValid(false);
                        }
                        handleChange(e as React.ChangeEvent<HTMLInputElement>);
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="open calculator"
                            onClick={handleCalculatorOpen}
                            disabled={isInventoryLocked}
                          >
                            <Icon path={mdiCalculator} size={0.8} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    disabled={isInventoryLocked}
                    error={errors.count}
                    helperText={
                      errors.count
                        ? countValidation
                          ? "Count should be greater than 0 and less than or equal to 99999999.99"
                          : "Count is required"
                        : ""
                    }
                    sx={{
                      "& .MuiInputBase-root": {
                        background: "#fff",
                      },
                      "& .MuiInputBase-input": {
                        background: "#fff",
                      },
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid size={{xs:5.5, sm:4}}>
                <FormControl fullWidth>
                  <Autocomplete
                    id="uom"
                    options={uoms}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.name || ""
                    }
                    value={
                      uoms.find((uom) => uom.name === formValues.uom) || null
                    }
                    onChange={(event, newValue) => {
                      setFormValues((prevValues) => {
                        if (typeof newValue === "string") {
                          return {
                            ...prevValues,
                            uom: newValue,
                            secondaryUomId: 0,
                          };
                        } else if (newValue) {
                          return {
                            ...prevValues,
                            uom: newValue.name,
                            secondaryUomId: 0,
                          };
                        } else {
                          return {
                            ...prevValues,
                            uom: "",
                            secondaryUomId: 0,
                          };
                        }
                      });

                      const isValidUom =
                        (newValue && typeof newValue === "string") ||
                        (newValue?.name &&
                          uoms.some((uom) => uom.name === newValue?.name));

                      // Set form validity if UOM is valid and count is provided
                      setIsFormValid(Boolean(formValues.count && isValidUom));
                    }}
                    disabled={isInventoryLocked}
                    onInputChange={(event, newInputValue) => {
                      setFormValues((prevValues) => ({
                        ...prevValues,
                        uom: newInputValue || "",
                        secondaryUomId: 0,
                      }));

                      // Check if the entered UOM is valid based on the uoms list
                      const isValidUom = uoms.some(
                        (uom) => uom.name === newInputValue
                      );
                      setIsFormValid(
                        Boolean(newInputValue && formValues.count && isValidUom)
                      );
                    }}
                    popupIcon={<Icon path={mdiChevronDown} size={1} />}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="UOM"
                        name="uom"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "Select UOM",
                        }}
                        error={Boolean(errors.uom)}
                        helperText={errors.uom ? "UOM is required" : ""}
                        disabled={isInventoryLocked}
                      />
                    )}
                    sx={{
                      "& .MuiInputBase-root": {
                        background: "#fff !important",
                      },
                      "& .MuiInputBase-input": {
                        background: "#fff !important",
                      },
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container className={styles.btnContainer} size={{xs:12, sm:4}}>
              <Button
                variant="contained"
                sx={{ padding: "20px 50px" }}
                onClick={onClickUpdateTagSubmit}
                disabled={!isFormValid || isInventoryLocked || isContainerized}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
          )}
        </Grid>
      )}
            {!tagDetails.isReconciliation &&
        tagDetails.auditMade !== null &&
        tagDetails.countMade !== null &&
        (!tagDetails.exceptions || tagDetails.exceptions?.trim() === "") &&
        (
          <Alert icon={false} severity="info" sx={{marginBottom:"10px"}}>
            Both Count and Audit numbers match! Good Job!
          </Alert>
        )}
 {containerizedDetails && containerizedDetails.entries && (
  <Grid size={{xs:12}}>
    <CountEntryTable
      entries={containerizedDetails.entries}
      isPalletized={containerizedDetails.isPalletized}
      isCartoned={containerizedDetails.isCartoned}
      isLotted={containerizedDetails.isLotted}
      isSerialized={containerizedDetails.isSerialized}
      onEdit={handleEdit}
      onDelete={handleDelete}
      isAllowSubmit = {isAllowEdit}
      editRestrictionMessage = {editRestrictionMessage}
      isOnClickResolveException={true}
      onSubmit={handleExceptionResolveSubmit}
      isLocked={isInventoryLocked}
    />
  </Grid>
)}


      <Suspense fallback={<div>Loading...</div>}>
        <ConfirmationDialog
          count={
            finalType === ReconciliationTypes.Counter
              ? tagDetails.countMade
              : finalType === ReconciliationTypes.Auditer
              ? tagDetails.auditMade
              : 0
          }
          title="Make this as a Final Count?"
          message="Please note that after taking this action, your tag will be marked as closed. Make sure you have checked all the scenarios and confirmed the number."
          open={ConfirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={() => {
            if (
              tagDetails.auditMade !== null &&
              tagDetails.countMade !== null &&
              uoms.length > 0 &&
              tagDetails.auditUOM !== null &&
              tagDetails.countUOM !== null
            ) {
              let value = tagDetails.countMade;
              let newUOM = tagDetails.countUOM;
              if (finalType == ReconciliationTypes.Auditer) {
                value = tagDetails.auditMade;
                newUOM = tagDetails.auditUOM;
              }

              setFormValues((prevValues) => ({
                ...prevValues,
                count: value.toString(),
                uom: newUOM,
              }));
              setErrors((prevErrors) => ({
                ...prevErrors,
                count: false,
                uom: false,
              }));

              if (uoms.length > 0) {
                const uOMId: number =
                  uoms.find((x) => x.name == newUOM)?.id ?? 0;
                updateReconcileDetails(value, uOMId);
              }
            }
            setConfirmDialogOpen(false);
          }}
        />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>}>
        <Calculator
          open={openCalculator}
          onClose={handleCalculatorClose}
          onSubmit={handleCalculatorValue}
        />
      </Suspense>
            {openAddCount && (<AddCountDialog
              open={openAddCount}
              onClose={handleAddCountClose}
              onSubmit={handleSubmitTagWithCount}
              isPalletized = {containerizedDetails?.isPalletized ?? false}
              isCartoned = {containerizedDetails?.isCartoned ?? false}
              isSerialized = {containerizedDetails?.isSerialized ?? false}
              isLotted = {containerizedDetails?.isLotted ?? false}
              location={containerizedDetails?.locationName ?? ""}
             locationId={containerizedDetails?.locationId}
              item={containerizedDetails?.itemName ?? ""}
              tagId={Number(id) || 0}
              isAdmin={true}
              isEdit={true}
              uomId = {containerizedDetails?.uomId || 0}
              actionEditIndex = {actionEditIndex}
              isAllowSubmit = {isAllowEdit}
              isCounter={false}
              itemId={containerizedDetails?.itemId}
            />)}
    </div>
  );
};

export default ReconciliationTagTile;
